import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { useContent } from '../hooks/useContent';
import { RichText } from './RichText';
import { SectionHeading } from './SectionHeading';
import { Building2, ScrollText, Shield, BarChart3 } from 'lucide-react';
import { CredentialModal } from './CredentialModal';
import { mergeCredentials } from '../data/credentialDefaults';

const defaultData = {
  headline: 'Empowering communities since 1979',
  description:
    'The Education & Welfare Research Foundation (EWRF) is a registered NGO dedicated to uplifting underserved communities in Malaysia through education, counselling, welfare, and volunteer development.',
  vision:
    'A just and compassionate society where every individual, regardless of background, has access to quality education, emotional support, and the resources to thrive.',
  mission:
    'To empower underserved communities through sustainable programmes in education, counselling, and welfare – delivered by trained volunteers and guided by research and compassion.',
  heritage:
    'Founded in 1979 in Petaling Jaya, EWRF has grown from a small welfare group into a nationally recognised organisation touching over 15,000 lives across Malaysia.',
  quote:
    'The true measure of a society is how it treats its most vulnerable members. For 47 years, EWRF has answered that measure with action.',
};

const PILLARS = [
  { letter: 'V', title: 'Vision', accent: 'from-amber-400 to-orange-500', key: 'vision' },
  { letter: 'M', title: 'Mission', accent: 'from-rose-400 to-pink-500', key: 'mission' },
  { letter: 'H', title: 'Heritage', accent: 'from-violet-400 to-fuchsia-500', key: 'heritage' },
];

const CREDENTIAL_STYLE = {
  'registered-ngo': { Icon: Building2, color: 'from-blue-500 to-cyan-500' },
  'tax-exempt': { Icon: ScrollText, color: 'from-emerald-500 to-teal-500' },
  'child-safe-policy': { Icon: Shield, color: 'from-amber-500 to-orange-500' },
  'annual-audits': { Icon: BarChart3, color: 'from-rose-500 to-pink-500' },
};

const About = () => {
  const { data } = useContent('about', {});
  const { data: credData } = useContent('credentials', {});
  const d = { ...defaultData, ...(data && data.id ? data : {}) };
  const credentials = mergeCredentials(credData?.items).map((c) => ({ ...c, ...CREDENTIAL_STYLE[c.key] }));
  const [openCred, setOpenCred] = useState(null);

  const headlineParts = d.headline.split(' ');
  const lastWord = headlineParts.pop();
  const headlineRest = headlineParts.join(' ');

  return (
    <section id="about" className="py-20 md:py-28 bg-gradient-to-b from-white via-amber-50/40 to-white relative overflow-hidden" data-testid="about-section">
      {/* decorative blobs */}
      <div className="absolute -top-20 right-10 w-80 h-80 bg-amber-200/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 -left-20 w-80 h-80 bg-rose-200/30 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4">
        <SectionHeading
          eyebrow="Who We Are"
          title={headlineRest}
          accent={lastWord}
        />

        <div className="grid lg:grid-cols-5 gap-10 md:gap-14 mb-14 md:mb-16 items-start">
          {/* Left: description + quote */}
          <div className="lg:col-span-3 min-w-0">
            {/* Decorative eyebrow strip */}
            <div className="flex items-center gap-3 mb-5">
              <span className="h-[2px] w-10 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full" />
              <span className="text-[11px] uppercase tracking-[0.2em] text-amber-700 font-bold">Our Story</span>
            </div>

            <div className="about-prose">
              <RichText
                html={d.description}
                className="about-lede text-lg md:text-[1.35rem] text-gray-700 mb-8"
                plainClass="about-lede text-lg md:text-[1.35rem] text-gray-700 mb-8"
              />
            </div>

            {d.quote && (
              <figure className="relative bg-gradient-to-br from-[#0A1628] via-[#0D2444] to-[#08182B] rounded-3xl p-8 md:p-10 text-white shadow-2xl overflow-hidden">
                <div className="absolute -top-16 -right-10 w-48 h-48 bg-amber-400/25 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-12 -left-10 w-40 h-40 bg-rose-400/15 rounded-full blur-3xl"></div>
                <span
                  aria-hidden="true"
                  className="absolute top-3 left-5 text-[7rem] md:text-[9rem] font-serif leading-none text-amber-400/25 select-none pointer-events-none"
                >
                  &ldquo;
                </span>
                <blockquote className="relative mt-8 md:mt-10">
                  <RichText
                    html={d.quote}
                    className="about-quote text-base md:text-lg italic leading-relaxed text-white/95"
                    plainClass="about-quote text-base md:text-lg italic leading-relaxed text-white/95"
                  />
                </blockquote>
                <figcaption className="relative mt-5 flex items-center gap-3">
                  <div className="h-0.5 w-12 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full"></div>
                  <span className="text-xs uppercase tracking-widest text-amber-300 font-bold">EWRF Promise</span>
                </figcaption>
              </figure>
            )}
          </div>

          {/* Right: V / M / H pillars */}
          <div className="lg:col-span-2 space-y-4 md:space-y-5 min-w-0">
            {PILLARS.map((p) => (
              <div
                key={p.letter}
                className="group relative bg-white rounded-2xl p-5 md:p-6 shadow-md hover:shadow-xl border-2 border-gray-100 hover:border-transparent transition-all overflow-hidden"
              >
                <div className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${p.accent}`}></div>
                <div className="flex gap-4 items-start">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${p.accent} flex items-center justify-center text-white font-black text-2xl shadow-lg flex-shrink-0 group-hover:scale-110 group-hover:rotate-3 transition-transform`}>
                    {p.letter}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-black text-[#0A1628] mb-1.5 uppercase tracking-widest text-xs">{p.title}</h4>
                    <RichText
                      html={d[p.key]}
                      className="about-pillar text-gray-600 text-sm leading-relaxed"
                      plainClass="about-pillar text-gray-600 text-sm leading-relaxed"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Credential strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {credentials.map((c) => (
            <button
              key={c.title}
              type="button"
              onClick={() => setOpenCred(c.key)}
              className="text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1565C0] rounded-2xl"
              data-testid={`credential-${c.key}`}
            >
              <Card className="group border-2 border-gray-100 hover:border-transparent hover:shadow-2xl transition-all overflow-hidden cursor-pointer h-full">
                <CardContent className="p-5 md:p-6 text-center relative">
                  <div className={`inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br ${c.color} text-white mb-3 shadow-lg group-hover:scale-110 group-hover:-rotate-3 transition-transform`}>
                    <c.Icon className="w-7 h-7 md:w-8 md:h-8" />
                  </div>
                  <h4 className="font-black text-[#0A1628] mb-1 text-sm md:text-base uppercase tracking-wide">{c.title}</h4>
                  <p className="text-[11px] md:text-xs text-gray-500 font-medium">{c.subtitle}</p>
                  <p className="text-[10px] uppercase tracking-widest text-[#1565C0] font-black mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Learn more →</p>
                </CardContent>
              </Card>
            </button>
          ))}
        </div>
      </div>
      <CredentialModal credentialKey={openCred} onClose={() => setOpenCred(null)} />
    </section>
  );
};

export default About;
